/**
 * TypeScript Code Editor with Syntax Highlighting
 *
 * Same dual-layer architecture as JsonEditor: transparent textarea
 * over a syntax-highlighted div. No external dependencies.
 */

import React, { useRef, useCallback } from 'react';

// ============================================================================
// TypeScript Syntax Highlighting
// ============================================================================

const TS_COLORS = {
    keyword: '#c586c0',      // Purple for keywords
    control: '#c586c0',      // Purple for control flow
    builtin: '#4ec9b0',      // Teal for built-in types
    function: '#dcdcaa',     // Yellow for function names
    string: '#ce9178',       // Orange for strings
    number: '#b5cea8',       // Light green for numbers
    comment: '#6a9955',      // Green for comments
    type: '#4ec9b0',         // Teal for type annotations
    punctuation: '#d4d4d4',  // Gray for punctuation
    identifier: '#9cdcfe',   // Light blue for identifiers
    default: '#d4d4d4',      // Default text
};

const KEYWORDS = new Set([
    'function', 'export', 'const', 'let', 'var', 'if', 'else', 'return',
    'await', 'async', 'for', 'while', 'do', 'break', 'continue',
    'import', 'from', 'of', 'in', 'new', 'this', 'typeof', 'instanceof',
    'throw', 'try', 'catch', 'finally', 'switch', 'case', 'default',
    'yield', 'class', 'extends', 'super',
]);

const TYPE_KEYWORDS = new Set([
    'type', 'interface', 'enum', 'namespace', 'readonly', 'as',
]);

const BUILTIN_TYPES = new Set([
    'number', 'string', 'boolean', 'void', 'null', 'undefined',
    'any', 'never', 'unknown', 'object', 'Promise',
]);

const CONSTANTS = new Set(['true', 'false', 'null', 'undefined', 'Math', 'JSON']);

const SyntaxHighlightedTs: React.FC<{ source: string }> = ({ source }) => {
    const highlight = (text: string): React.ReactNode[] => {
        const result: React.ReactNode[] = [];
        let i = 0;
        let key = 0;

        const push = (content: string, color: string) => {
            result.push(<span key={key++} style={{ color }}>{content}</span>);
        };

        while (i < text.length) {
            // Line comments
            if (text[i] === '/' && text[i + 1] === '/') {
                let end = text.indexOf('\n', i);
                if (end === -1) end = text.length;
                push(text.slice(i, end), TS_COLORS.comment);
                i = end;
                continue;
            }

            // Block comments
            if (text[i] === '/' && text[i + 1] === '*') {
                let end = text.indexOf('*/', i + 2);
                if (end === -1) end = text.length;
                else end += 2;
                push(text.slice(i, end), TS_COLORS.comment);
                i = end;
                continue;
            }

            // Strings (single, double, template)
            if (text[i] === '"' || text[i] === "'" || text[i] === '`') {
                const quote = text[i];
                let str = quote;
                i++;
                while (i < text.length && text[i] !== quote) {
                    if (text[i] === '\\' && i + 1 < text.length) {
                        str += text[i] + text[i + 1];
                        i += 2;
                    } else {
                        str += text[i];
                        i++;
                    }
                }
                if (i < text.length) {
                    str += text[i];
                    i++;
                }
                push(str, TS_COLORS.string);
                continue;
            }

            // Numbers
            if (/\d/.test(text[i]) || (text[i] === '.' && i + 1 < text.length && /\d/.test(text[i + 1]))) {
                let num = '';
                while (i < text.length && /[\d.eE_xXa-fA-F]/.test(text[i])) {
                    num += text[i];
                    i++;
                }
                push(num, TS_COLORS.number);
                continue;
            }

            // Words (keywords, identifiers, types)
            if (/[a-zA-Z_$]/.test(text[i])) {
                let word = '';
                while (i < text.length && /[\w$]/.test(text[i])) {
                    word += text[i];
                    i++;
                }

                if (KEYWORDS.has(word)) {
                    push(word, TS_COLORS.keyword);
                } else if (TYPE_KEYWORDS.has(word)) {
                    push(word, TS_COLORS.type);
                } else if (BUILTIN_TYPES.has(word)) {
                    push(word, TS_COLORS.builtin);
                } else if (CONSTANTS.has(word)) {
                    push(word, TS_COLORS.builtin);
                } else {
                    // Check if followed by ( — likely a function call/declaration
                    let j = i;
                    while (j < text.length && /\s/.test(text[j])) j++;
                    if (text[j] === '(') {
                        push(word, TS_COLORS.function);
                    } else {
                        push(word, TS_COLORS.identifier);
                    }
                }
                continue;
            }

            // Whitespace (preserve)
            if (/\s/.test(text[i])) {
                let ws = '';
                while (i < text.length && /\s/.test(text[i])) {
                    ws += text[i];
                    i++;
                }
                result.push(<span key={key++}>{ws}</span>);
                continue;
            }

            // Punctuation / operators
            push(text[i], TS_COLORS.punctuation);
            i++;
        }

        return result;
    };

    return <>{highlight(source)}</>;
};

// ============================================================================
// Script Editor Component
// ============================================================================

export interface ScriptEditorProps {
    value: string;
    onChange: (value: string) => void;
    error?: string | null;
    minHeight?: string;
}

const textStyles: React.CSSProperties = {
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '12px',
    lineHeight: '18px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    letterSpacing: 'normal',
    tabSize: 2,
};

export const ScriptEditor: React.FC<ScriptEditorProps> = ({
    value,
    onChange,
    error,
    minHeight,
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const highlightRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(() => {
        if (textareaRef.current && highlightRef.current) {
            highlightRef.current.scrollTop = textareaRef.current.scrollTop;
            highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
    }, []);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Tab inserts two spaces instead of focusing next element
        if (e.key === 'Tab') {
            e.preventDefault();
            const textarea = e.currentTarget;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const newValue = value.slice(0, start) + '  ' + value.slice(end);
            onChange(newValue);
            // Restore cursor position after React re-render
            requestAnimationFrame(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 2;
            });
        }
    }, [value, onChange]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {error && (
                <div style={{
                    padding: '8px 12px',
                    marginBottom: '8px',
                    backgroundColor: '#5c2020',
                    border: '1px solid #ff5252',
                    color: '#ff8a80',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontFamily: textStyles.fontFamily,
                    whiteSpace: 'pre-wrap',
                }}>
                    {error}
                </div>
            )}
            <div style={{
                flex: 1,
                ...(minHeight ? { minHeight } : {}),
                position: 'relative',
                backgroundColor: '#1e1e1e',
                border: '1px solid #444',
                borderRadius: '4px',
                overflow: 'hidden',
            }}>
                {/* Syntax highlighted layer (behind) */}
                <div
                    ref={highlightRef}
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        padding: '12px',
                        margin: 0,
                        border: 'none',
                        pointerEvents: 'none',
                        overflow: 'auto',
                        color: TS_COLORS.default,
                        ...textStyles,
                    }}
                >
                    <SyntaxHighlightedTs source={value} />
                </div>
                {/* Editable textarea layer (front, transparent text) */}
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onScroll={handleScroll}
                    onKeyDown={handleKeyDown}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        padding: '12px',
                        margin: 0,
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'transparent',
                        caretColor: '#fff',
                        resize: 'none',
                        outline: 'none',
                        overflow: 'auto',
                        ...textStyles,
                    }}
                />
            </div>
        </div>
    );
};
